import React, { useState } from 'react';
import { ChevronDown, Sparkles, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * DescriptionPromptsHelper - Single text input with expandable suggestion chips
 *
 * ONE text input is always visible. The "Need inspiration?" button expands
 * to reveal suggestion chips organized by category. Clicking a chip builds
 * a proper sentence in the text input.
 *
 * @param {Object} prompts - Optional class-specific prompts by category
 * @param {Object} currentSelections - Current selections { clothes, eyes, body, skin, attitude }
 * @param {Function} onSelectVariable - Callback(category, value) when user types or clicks a prompt
 * @param {string} combinedValue - The combined appearance string
 * @param {Function} onCombinedChange - Callback(value) when the combined text changes
 */
function DescriptionPromptsHelper({
  prompts,
  currentSelections = {},
  onSelectVariable,
  combinedValue = '',
  onCombinedChange
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasPrompts = prompts && Object.keys(prompts).length > 0;

  // Build a proper sentence from all current selections
  const buildSentence = (selections) => {
    const parts = [];

    if (selections.body) {
      parts.push(`${selections.body} build`);
    }
    if (selections.eyes) {
      parts.push(`${selections.eyes} eyes`);
    }
    if (selections.skin) {
      parts.push(`${selections.skin} skin`);
    }
    if (selections.clothes) {
      parts.push(`wearing ${selections.clothes}`);
    }
    if (selections.attitude) {
      parts.push(`with ${selections.attitude}`);
    }

    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;

    const lastPart = parts.pop();
    return `${parts.join(', ')}, and ${lastPart}`;
  };

  // Handle chip click - store discrete value and rebuild sentence
  const handleChipClick = (category, option) => {
    // Store the discrete value for AI
    onSelectVariable(category, option);

    // Build new sentence with this selection
    const newSelections = {
      ...currentSelections,
      [category]: option
    };

    if (onCombinedChange) {
      const sentence = buildSentence(newSelections);
      onCombinedChange(sentence);
    }
  };

  // Handle direct text input
  const handleTextChange = (value) => {
    if (onCombinedChange) {
      onCombinedChange(value);
    }
  };

  return (
    <div className="space-y-3">
      {/* Need Inspiration? - Only show if prompts exist */}
      {hasPrompts && (
        <div>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors text-sm font-medium"
          >
            <Sparkles className="w-4 h-4" />
            Need inspiration?
            <ChevronDown className={cn(
              "w-4 h-4 transition-transform",
              isExpanded && "rotate-180"
            )} />
          </button>

          {isExpanded && (
            <div className="mt-3 p-4 bg-gradient-to-br from-gray-900 to-black border border-purple-500/50 rounded-lg space-y-3">
              <p className="text-xs text-gray-400">
                Click options to build your appearance description.
              </p>

              {Object.entries(prompts).map(([category, options]) => (
                <div key={category}>
                  <h5 className="text-xs font-semibold text-purple-300 mb-2">
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </h5>
                  <div className="flex flex-wrap gap-2">
                    {options.map((option, idx) => {
                      const isSelected = currentSelections[category] === option;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleChipClick(category, option)}
                          className={cn(
                            "px-2 py-1 text-xs rounded-full transition-all flex items-center gap-1",
                            isSelected
                              ? "bg-purple-600 text-white"
                              : "bg-purple-600/20 text-purple-300 hover:bg-purple-600/40"
                          )}
                        >
                          {isSelected && <Check className="w-3 h-3" />}
                          {option}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Always visible single text input - REQUIRED */}
      <textarea
        value={combinedValue}
        onChange={(e) => handleTextChange(e.target.value)}
        placeholder="Describe your character's appearance..."
        rows={2}
        className="w-full px-3 py-2 text-sm bg-surface-2 border border-border-default rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
      />
    </div>
  );
}

export default DescriptionPromptsHelper;
